// @flow
import React, { PureComponent } from "react"
import { connect } from "react-redux"
import classnames from "classnames"
import { FacebookShareButton, TwitterShareButton, FacebookIcon, TwitterIcon } from "react-share"
import moment from "moment"
import Clipboard from "clipboard"
import memoize from "lru-memoize"
import Link from "@kiwicom/orbit-components/lib/icons/Link"

import cuckoo from "common/services/cuckoo/cuckoo"
import jsonFetch from "common/services/fetch/jsonFetch"
import { isMobileSelector } from "common/services/window"
import { getLanguageInfo } from "common/services/staticData"
import { getFormat } from "common/components/Timestamp/services/timestampTools"
import { setNotice as _setNotice } from "common/services/notice"
import type Journey from "common/records/Journey"
import type { LanguageInfo } from "common/records/LanguageInfo"
import MoveToBody from "../MoveToBody/MoveToBody"
import Popup from "common/components/Popup/Popup"
import translate from "../../../../app/scripts/tools/translate"
import Translate from "../../../../app/scripts/components/Translate"

type Props = {
  journey: Journey,
  updatePosition: (*) => void,
  onClose: () => void,
  onToggle: () => void,
  detached: boolean,
  isMobile: boolean,
  url: string,
  language: LanguageInfo,
  setNotice: typeof _setNotice,
  fixed?: boolean,
}

type State = {|
  shortUrl: string,
|}

const media = [
  { label: "Facebook", component: FacebookShareButton, icon: FacebookIcon, key: "facebook" },
  { label: "twitter", component: TwitterShareButton, icon: TwitterIcon, key: "twitter" },
  { label: __("search.share_modal.copy_url"), key: "copy" }, // !TRANS-COLLECT:add=search.share_modal.copy_url
]

const COPIED_LINK_SUCCESS_TIMEOUT = 3500

const fetchShortUrl = memoize(10)((url: string) => jsonFetch(`/u?url=${encodeURIComponent(url)}`))

class ShareJourneyDialog extends PureComponent<Props, State> {
  state = {
    shortUrl: "",
  }

  componentDidMount() {
    const { url, journey } = this.props
    this.clipboard = new Clipboard(".ShareJourneyDialog-socialItem.copy", {
      target: () => this.urlInput,
    })

    this.clipboard.on("success", this.onClipboardCopy)
    this.clipboard.on("error", this.preSelect)

    this.setShortUrl(url, journey)
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.url !== this.props.url) {
      this.setState({ shortUrl: "" })
      this.setShortUrl(nextProps.url, nextProps.journey)
    }
  }

  componentWillUnmount() {
    this.clipboard.destroy()
  }

  onClipboardCopy = () => {
    const { onClose, setNotice } = this.props

    onClose()
    setNotice({
      id: "shareCopy",
      type: "success",
      title: <Translate tKey="search.link_copied" />,
      ttl: COPIED_LINK_SUCCESS_TIMEOUT,
    })
  }

  setShortUrl = async (url: string, journey: Journey) => {
    const shareUrl = `${url}#${journey.getId()}`

    const { shortUrl } = await fetchShortUrl(shareUrl).catch(() => ({
      shortUrl: `${window.location.origin}${shareUrl}`,
    }))

    this.setState({ shortUrl })
  }

  getQuote = () => {
    const { journey, language } = this.props
    const departure = journey.getDeparture()
    const departurePlace = departure.get("where")
    const departureTime = departure.getIn(["when", "local"])
    const lastTrip = journey.get("trips").last()
    const arrivalPlace = journey.isReturn()
      ? lastTrip.getDeparture().get("where")
      : lastTrip.getArrival().get("where")
    const arrivalTime = lastTrip.getDeparture().getIn(["when", "local"])

    moment.locale(language.moment)

    const format = getFormat(language, "${dateFormatLong}")

    const commonValues = {
      from: departurePlace.get("name"),
      to: arrivalPlace.get("name"),
      start: departureTime.format(format),
      end: arrivalTime.format(format),
    }

    if (journey.isMulticity()) {
      const stops = journey.trips.count() - 1
      if (departurePlace.get("name") === arrivalPlace.get("name")) {
        return stops === 1
          ? translate("search.share.multicity_return", {
              ...commonValues,
            })
          : translate("search.share.multicity_return_stops", {
              ...commonValues,
              stops,
            })
      }
      return stops === 1
        ? translate("search.share.multicity_one_way", {
            ...commonValues,
          })
        : translate("search.share.multicity_one_way_stops", {
            ...commonValues,
            stops,
          })
    }

    return journey.isReturn()
      ? translate("search.share.return", commonValues)
      : translate("search.share.one_way", commonValues)
  }

  stopPropagation = (e: SyntheticEvent<*>) => {
    e.stopPropagation()
    e.preventDefault()
  }

  saveInputRef = (ref: ?HTMLInputElement) => {
    this.urlInput = ref
  }

  preSelect = () => {
    if (this.urlInput) {
      const el = this.urlInput

      el.focus()
      el.select()
      el.setSelectionRange(0, 9999)
    }
  }

  clipboard: Clipboard

  urlInput: ?HTMLInputElement

  getShareDialogContent = () => {
    const { onClose, isMobile, detached, updatePosition } = this.props
    const { shortUrl } = this.state
    const quote = this.getQuote()

    return (
      <Popup
        className="ShareJourneyDialog-popup"
        onHide={onClose}
        onContentDidMount={isMobile || !detached ? null : updatePosition}
      >
        <div
          className={classnames("ShareJourneyDialog-popup-wrapper", { _loading: !shortUrl })}
          onClick={this.stopPropagation}
        >
          <div className="ShareJourneyDialog-buttons">
            {media.map(Item => (
              <div
                key={Item.key}
                className={`ShareJourneyDialog-socialItem ${Item.key}`}
                onClick={() => {
                  cuckoo.infinario("shareFlight", { action: "clickOnMedium", medium: Item.key })
                }}
              >
                {Item.component ? (
                  <Item.component url={shortUrl} quote={quote} title={quote}>
                    <Item.icon size={68} />
                    <div className="ShareJourneyDialog-socialItem-label">{Item.label}</div>
                  </Item.component>
                ) : (
                  <div className="ShareJourneyDialog-socialItem-link">
                    <div className="ShareJourneyDialog-socialItem-link-icon">
                      <Link size="large" />
                    </div>
                    <div className="ShareJourneyDialog-socialItem-label">
                      <Translate tKey={Item.label} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="ShareJourneyDialog-input-wrapper">
            <input
              type="text"
              value={shortUrl}
              id="ShareJourneyDialog-input"
              readOnly
              dir="ltr"
              onClick={this.preSelect}
              ref={this.saveInputRef}
            />
          </div>
        </div>
      </Popup>
    )
  }

  render() {
    const { onClose, isMobile, detached, fixed, onToggle } = this.props

    return (
      <div className="ShareJourneyDialog-wrapper" onClick={onToggle}>
        {isMobile || detached ? (
          <MoveToBody className={classnames("ShareJourneyDialog", "_detached", { _fixed: fixed })}>
            {this.getShareDialogContent()}
            {isMobile && <div className="ShareJourneyDialog-overlay" onClick={onClose} />}
          </MoveToBody>
        ) : (
          <div className="ShareJourneyDialog">{this.getShareDialogContent()}</div>
        )}
      </div>
    )
  }
}

// $FlowFixMe TODO: plz add type when updating
export default connect(
  state => ({
    language: getLanguageInfo(state),
    isMobile: isMobileSelector(state),
  }),
  { setNotice: _setNotice },
)(ShareJourneyDialog)
